import React, { useMemo } from 'react';
import { RiErrorWarningLine } from '@remixicon/react';
import type { BackendSegment, BackendSegmentCondition, EventRule } from '@/types';
import { validateSegmentRules, type RuleDraft } from '@/utils/segmentValidation';

function formatCondition(condition: BackendSegmentCondition, prefix: 'Where' | 'And' | 'Or' = 'Where'): string {
  const prop = condition.property_name ?? condition.property_key ?? String(condition.property_id ?? 'Property');
  const op = condition.operator ?? '=';
  const vals = Array.isArray(condition.value) ? condition.value : [];
  const valueStr = vals.length > 0 ? vals.join(', ') : '';
  return `${prefix} ${prop} ${op} ${valueStr}`.trim();
}

function formatEventRule(rule: EventRule): string {
  const event = rule.event ?? 'event';
  const selector = rule.selector_value ?? rule.selector_pattern ?? '';
  const prop = rule.property ? ` (${rule.property})` : '';
  const op = rule.operator ? ` ${rule.operator}` : '';
  const vals = Array.isArray(rule.value) && rule.value.length > 0 ? ` ${rule.value.join(', ')}` : '';
  return `${event}${selector ? ` on ${selector}` : ''}${prop}${op}${vals}`.trim() || 'Event rule';
}

function conditionsToRuleDrafts(conditions: BackendSegmentCondition[]): RuleDraft[] {
  return conditions.map((c, i) => ({
    id: `cond-${i}-${c.id ?? ''}`,
    propertyKey: String(c.property_id ?? c.property_key ?? ''),
    operator: c.operator ?? '=',
    value: Array.isArray(c.value) ? c.value : [],
  }));
}

interface SegmentReadOnlyViewProps {
  segment: BackendSegment | null;
  className?: string;
}

export function SegmentReadOnlyView({ segment, className = '' }: SegmentReadOnlyViewProps) {
  const conditions = useMemo(
    () => segment?.session_filters ?? segment?.conditions ?? [],
    [segment]
  );
  const eventRules = useMemo(
    () => segment?.event_filters ?? segment?.event_rules ?? [],
    [segment]
  );

  const ruleDrafts = useMemo(() => conditionsToRuleDrafts(conditions), [conditions]);
  const validation = useMemo(() => validateSegmentRules(ruleDrafts), [ruleDrafts]);
  const conflictingRuleIds = useMemo(() => {
    const ids = new Set<string>();
    validation.errors.forEach((e) => e.ruleIds.forEach((id) => ids.add(id)));
    return ids;
  }, [validation.errors]);

  if (!segment) {
    return (
      <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
        No segment data available.
      </p>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {segment.description && (
        <p className="text-default-500 text-sm">{segment.description}</p>
      )}

      {!validation.valid && validation.errors.length > 0 && (
        <div
          role="alert"
          className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-950/40"
        >
          <RiErrorWarningLine className="size-5 shrink-0 text-amber-600 dark:text-amber-500" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Logical conflict detected
            </p>
            <ul className="list-inside list-disc space-y-0.5 text-sm text-amber-700 dark:text-amber-300">
              {validation.errors.map((err, idx) => (
                <li key={idx}>{err.message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-default-700 mb-2 text-sm font-semibold">Session rules</h4>
        {conditions.length === 0 ? (
          <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
            No session rules defined.
          </p>
        ) : (
          <div className="space-y-2">
            {(conditions as BackendSegmentCondition[]).map((cond, i) => {
              const ruleId = ruleDrafts[i]?.id;
              const isConflicting = ruleId && conflictingRuleIds.has(ruleId);
              return (
                <div
                  key={i}
                  className={`rounded-lg px-4 py-3 transition-colors ${
                    isConflicting
                      ? 'border-2 border-red-300 bg-red-50/60 dark:border-red-700/60 dark:bg-red-950/30'
                      : 'border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                  }`}
                >
                  {formatCondition(cond, i === 0 ? 'Where' : 'And')}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {eventRules.length > 0 && (
        <div>
          <h4 className="text-default-700 mb-2 text-sm font-semibold">Event rules</h4>
          <div className="space-y-2">
            {(eventRules as EventRule[]).map((rule, i) => (
              <div
                key={i}
                className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50"
              >
                {formatEventRule(rule)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
